/**
 * Magics played to the storyline. A magic is cast TO the storyline and
 * resolves there, then settles on the square it was cast to. This layer shows
 * each casting spell's card art flying from the play area into the storyline
 * anchor (the same right-edge slot the EffectPromptDrawer / StorylinePile use)
 * and dwelling while it resolves; once it has resolved the entry is removed.
 *
 * Purely cosmetic. Driven by `state.castingSpells`, separate from
 * pendingPrompts so it can never block a prompt or the turn-pass. The card's
 * actual landing is the standard addCardToTable path (castMagicFromHand →
 * applyCardAutomation → landCastSpellOnBoard), not anything here.
 *
 * Props: casts. [{ id, cardName, imageUrl, rotated }].
 */
import { motion, AnimatePresence } from 'framer-motion';
import { GOLD, RADIUS } from '../../lib/medievalTheme';

const W_CQW = 14;
const H_CQW = 19;
// Above the receding pile (58) and vignette (59); the active drawer is 60.
const Z = 59;

function CastCard({ cast }) {
  const wrapperStyle = {
    position: 'absolute',
    right: '1.5cqw',
    top: '50%',
    width: `${W_CQW}cqw`,
    height: `${H_CQW}cqw`,
    borderRadius: RADIUS.card,
    overflow: 'hidden',
    backgroundColor: '#0e0c08',
    boxShadow: '0 0.5cqw 1.5cqw rgba(0,0,0,0.7), 0 0 0 1px rgba(232, 195, 110, 0.5)',
    pointerEvents: 'none',
    willChange: 'transform, opacity',
  };
  return (
    <motion.div
      key={cast.id}
      // Fly in from the play area (left + below) into the storyline anchor.
      // y MUST keep the same `calc(-50% + Ncqw)` form on every keyframe.
      // framer-motion only tweens between matching value forms, so mixing
      // `calc(...)` with a plain `-50%` would SNAP (the card popped in with
      // no flight). x stays a plain cqw on every keyframe for the same reason.
      initial={{ opacity: 0, x: '-26cqw', y: 'calc(-50% + 18cqw)', scale: 1.3, rotate: -6 }}
      animate={{
        opacity: 1,
        x: '0cqw',
        y: 'calc(-50% + 0cqw)',
        scale: 1,
        rotate: 0,
        transition: { type: 'spring', stiffness: 140, damping: 20 },
      }}
      // On leave-play: drift toward the cemetery (down-right) and fade.
      exit={{
        opacity: 0,
        x: '6cqw',
        y: 'calc(-50% + 22cqw)',
        scale: 0.8,
        transition: { duration: 0.4, ease: 'easeIn' },
      }}
      style={wrapperStyle}
    >
      {cast.imageUrl ? (
        <img
          src={cast.imageUrl}
          alt={cast.cardName}
          draggable={false}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <div
          style={{
            width: '100%', height: '100%', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: GOLD, fontSize: '1cqw', textAlign: 'center', padding: '0.5cqw',
          }}
        >
          {cast.cardName}
        </div>
      )}
    </motion.div>
  );
}

export default function SpellCastFlightLayer({ casts }) {
  if (!casts || casts.length === 0) return null;
  return (
    <div aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: Z, pointerEvents: 'none' }}>
      <AnimatePresence>
        {casts.map((cast) => (
          <CastCard key={cast.id} cast={cast} />
        ))}
      </AnimatePresence>
    </div>
  );
}
